import DonorNumberCard from "./DonorNumberCard";
import { Sparkles } from "lucide-react";
import { cities } from "@/data/cities";

const getProviderLocations = (providerName: string) => {
  const providerCities = cities.flatMap((city) => {
    const provider = city.providers.find((item) => item.name === providerName);
    if (!provider) return [];
    return [{ city: city.name, centerCount: provider.addresses.length }];
  });

  return {
    cities: providerCities.map(({ city, centerCount }) =>
      centerCount > 1 ? `${city} (${centerCount}x)` : city,
    ),
    summary: `${providerCities.length} Städte · ${providerCities.reduce((sum, city) => sum + city.centerCount, 0)} Zentren`,
  };
};

const getProviderProfile = (providerName: string) =>
  cities
    .flatMap((city) => city.providers)
    .find((provider) => provider.name === providerName)!;

const BonusSection = () => {
  const pseLocations = getProviderLocations("Plasma Service Europe");
  const cslLocations = getProviderLocations("CSL Plasma");
  const pse = getProviderProfile("Plasma Service Europe");
  const csl = getProviderProfile("CSL Plasma");

  return (
    <section
      className="section-padding bg-background relative overflow-hidden py-16 md:py-20 scroll-mt-20 md:scroll-mt-0"
      id="bonus"
      aria-labelledby="bonus-heading"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-bonus/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="container container-narrow relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bonus-gradient rounded-full px-5 py-2.5 mb-6 glow-bonus">
            <Sparkles className="w-4 h-4 text-bonus-foreground" />
            <span className="text-sm font-bold text-bonus-foreground">
              Exklusive Boni {new Date().getFullYear()}
            </span>
          </div>

          <h2
            id="bonus-heading"
            className="text-3xl md:text-4xl font-bold mb-5 font-display"
          >
            <span className="text-foreground">Bonusprogramme für </span>
            <span className="text-gradient-warm">neue Spender</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg">
            Plasmazentren in Deutschland begrüßen neue Spender mit attraktiven
            Bonusprogrammen. Nutzen Sie eine Spendernummer bei der Anmeldung, um
            von einem attraktiven Anmeldebonus zu profitieren.
          </p>
        </div>

        {/* Provider Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          <DonorNumberCard
            provider="Plasma Service Europe"
            description="Plasma Service Europe ist einer der führenden Anbieter für Plasmaspenden in Deutschland mit modernen Zentren in 13 Städten. Neuspender erhalten bis zu 100 € Anmeldebonus, gestaffelt über die ersten Spenden. Zusätzlich gibt es regelmäßig Sonderaktionen mit weiteren Boni."
            donorNumber={pse.donorNumber}
            bonusInfo={pse.bonusInfo}
            locations={pseLocations.cities}
            locationSummary={pseLocations.summary}
            accentColor="primary"
          />

          <DonorNumberCard
            provider="CSL Plasma"
            description="CSL Plasma gehört zum weltweit führenden Biotechnologieunternehmen CSL Behring und betreibt über 15 Zentren in Deutschland. Neuspender erhalten bis zu 150 € Anmeldebonus, gestaffelt über die ersten Spenden."
            donorNumber={csl.donorNumber}
            bonusInfo={csl.bonusInfo}
            locations={cslLocations.cities}
            locationSummary={cslLocations.summary}
            accentColor="bonus"
          />
        </div>

        {/* Disclaimer */}
        <p className="text-sm text-muted-foreground text-center mt-10 max-w-2xl mx-auto">
          Die Bonusprogramme und Konditionen werden von den jeweiligen Anbietern
          festgelegt und können sich ändern. Bitte informieren Sie sich bei der
          Anmeldung über aktuelle Aktionen.
        </p>
      </div>
    </section>
  );
};

export default BonusSection;
